import {
  Component,
  Input,
  Output,
  EventEmitter,
  HostListener,
  HostBinding,
  ElementRef,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { Subscription } from "rxjs";
import { tr } from "@util/tr";
import { toast } from "@web/util/toast";
import { AccountsService } from "@services/accounts.service";

@Component({
  selector: "ws-button",
  templateUrl: "./ws-button.component.html",
  styleUrls: ["./ws-button.component.scss"],
})
export class WsButtonComponent implements OnInit, OnDestroy {
  @Input() permission: string = "";
  @Input() tooltip: string = "";
  @Input() textColor?: string;
  @Input() backgroundColor?: string;
  @Input() width?: string;
  @Input() callToActionType?: 
    | "primary"
    | "secondary"
    | "tertiary"
    | "danger"
    | "warning"
    | "info"
    | "success"
    | "link";

  @Input() disabled: boolean = false;
  @Input() debounceTime: number = 300; // Default debounce time of 300ms

  @Output() click: EventEmitter<MouseEvent> = new EventEmitter<MouseEvent>();

  private isProcessing: boolean = false;
  private permissionSubscription?: Subscription;

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly accounts: AccountsService
  ) {}

  ngOnInit(): void {
    this.permissionSubscription = this.accounts.cachedPermissions$.subscribe();
  }

  ngOnDestroy(): void {
    this.permissionSubscription?.unsubscribe();
  }

  get effectiveTooltip(): string {
    if (this.isPermissionDenied()) {
      return tr("You do not have permission for this action");
    }
    return this.tooltip || "";
  }

  private isPermissionDenied(): boolean {
    if (this.host.nativeElement.getAttribute("dws-permission-denied") === "true") {
      return true;
    }
    if (
      this.permission &&
      this.accounts.hasCachedPermissions() &&
      !this.accounts.hasPermission(this.permission)
    ) {
      return true;
    }
    return false;
  }

  @HostBinding("class.disabled")
  get isDisabled(): boolean {
    return this.disabled;
  }

  @HostListener("click", ["$event"])
  onHostClick(event: MouseEvent): void {
    // Stop bubble to parent rows/cards, but do not preventDefault /
    // stopImmediatePropagation: those break MatMenuTrigger on this host (N-7 / N-7b).
    event.stopPropagation();
    this.onClick(event);
  }

  onClick(event?: MouseEvent): void {
    if (this.disabled || this.isProcessing) {
      if (event) {
        event.stopPropagation();
      }
      return;
    }

    this.isProcessing = true;

    try {
      this.click.emit(event);
    } catch (error) {
      toast(tr("An error occurred. Try again later!"));
      console.error("An error occurred during button click:", error);
    } finally {
      setTimeout(() => {
        this.isProcessing = false;
      }, this.debounceTime);
    }
  }

  get isCallToAction(): boolean {
    return !!this.callToActionType;
  }
}
